// packages/web/src/process/SwimlaneView.tsx
//
// Hybrid HTML grid + SVG overlay renderer for SwimlaneLayout.
//
// Blocks live as HTML divs inside a CSS grid (one column per lane,
// one row per layout y). ALL geometry — row heights, block rects,
// wrapped text lines — comes from the pure `swimlaneGeometry` pass
// (@archlang/scene/process, Inter metrics), the same pass the headless
// SVG export consumes, so live and export cannot drift. The grid rows
// get explicit heights and each cell renders the pre-wrapped lines;
// nothing is measured from the DOM. Frames, arrows, and lane
// separators are an SVG overlay reading the same geometry.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  SwimlaneLayout,
  WalkerState,
  WalkResult,
  NodeId,
  Row,
  Frame,
  StepLatency,
} from "@archlang/engine";
import { metricsMeasure } from "@archlang/scene";
import {
  swimlaneGeometry,
  swimlaneTopology,
  swimlaneRouting,
  swimlaneFlowEdgeGeom,
  swimlaneFlowAux,
  swimlaneGatewayGeom,
  swimlaneSeqArcs,
  commentLabel,
  LANE_WIDTH,
  LANE_HEADER_H,
  type SwimlaneGwGeom,
  type SwimlaneAuxPath,
  type SwimlaneSeqArc,
} from "@archlang/scene/process";
import { svgFragmentUrl } from "../svgFragmentUrl";
import { isFrameExclusive } from "./frameAffordance";
import { labelForStep } from "./labelForStep";
import type { ProcessStepEditApi } from "./processStepEdit.js";
import { clickModifiers, type ProcessSelection } from "./processSelection.js";
import {
  buildFlowGraph,
  emptyFlowGraph,
  shouldAnimateTransition,
} from "./flow-graph";
import {
  laneColorById as buildLaneColorById,
  makeLaneLabel,
  makeRowOccupancy,
} from "./swimlaneLanes.js";
import {
  isCommentRow,
  isFirstRealStep,
  computeRowByNodeKey,
  computeTriggerRowIndexes,
  computeWaitRowIndexes,
  blockYBounds,
  resolveFlowAuxEdges,
  resolveSeqReentryEdges,
  resolveSeqCompEdges,
  type RowGeometry,
} from "./swimlaneRows.js";
import {
  GatewayArrows,
  StepDroplets,
  FlowArrow,
  SequenceArrow,
} from "./swimlaneArrows.js";
import {
  SubprocessBand,
  SubprocessRail,
  EachLoopRail,
  SubprocessSection,
  FrameGroup,
} from "./swimlaneFrames.js";
import { BlockCell, CommentCell, TerminatorCell } from "./swimlaneCells.js";
import { ConnectorInsert } from "./ConnectorInsert.js";
import type { InsertEnclosing } from "./insertScaffolds.js";
import { useStepDrag } from "./useStepDrag.js";
import { resolveVerticalDrop, resolveBlockDrop, type DragRow, type BlockZone } from "./processDragResolve.js";
import { treeDragModel } from "./treeDrag.js";
import { LaneHeaders, LaneSeparators } from "./swimlaneChrome.js";

// Every layout constant (lane/row/block/gutter/frame/arrow) and the comment
// ellipsis helper come from @archlang/scene/process — the same values the
// pure geometry pass and the headless SVG export are built from.
/** Auto-play dwell on each node AFTER the travel droplet reaches it, before
 *  advancing — so each step reads as "arrive, pause, move on". */
const AUTO_DWELL_MS = 500;

/**
 * Display magnification for the live flow / sequence views. The geometry pass
 * still runs at scale 1 (so the headless-parity pin is unaffected); this is a
 * presentation-only CSS `zoom` on the board wrapper — proven not to break the
 * sticky lane header (a `transform: scale` would, by establishing a containing
 * block). One knob, uniform: the headless renderers expose the finer
 * `fontScale` / `layoutScale` split via `swimlaneMetrics`.
 */
const PROCESS_VIEW_ZOOM = 1.2;

// Definition lives in swimlaneTypes; imported for local use and re-exported for
// back-compat (consumers useStepEdgeStyleResolver / BpmnView / tests import it
// from here).
import type { StepEdgeStyle } from "./swimlaneTypes.js";
export type { StepEdgeStyle };

// Insert-affordance geometry (WYSIWYG "+" hover zones). The "+" lives in the GAP
// below a step: `INSERT_GAP` is that band's height, `INSERT_INSET` the side inset
// for a sequence row-rule so it doesn't kiss the board edge.
const INSERT_GAP = 20;
const INSERT_INSET = 12;

export interface SwimlaneViewProps {
  readonly layout: SwimlaneLayout;
  readonly walkResult: WalkResult | null;
  readonly walkerState: WalkerState;
  readonly activeStepIndex: number | null;
  readonly onSelectStepIndex: (i: number | null) => void;
  readonly onBranchSelect: (nodeId: NodeId, label: string) => void;
  readonly onSubprocessToggle: (nodeId: NodeId, expanded: boolean) => void;
  readonly onSelectModule: (qualified: string) => void;
  readonly resolveStepEdgeStyle?: (stepIndex: number) => StepEdgeStyle | undefined;
  /** Auto-play in progress — the step-travel droplet animates slower than a
   *  manual next (which gets a snappier traversal). */
  readonly autoPlay?: boolean;
  /** Advance to the next step. The view calls this to drive the auto-play
   *  cadence: it waits for the droplet to REACH the node, then dwells, then
   *  advances — so the animation always plays fully (no fixed timer). */
  readonly onAutoAdvance?: () => void;
  /**
   * Owner-path → colour from the unified node-coloring layer (the "colour by"
   * picker: type by default, or a label key). Tints lane headers and block
   * borders by the lane's module; active/compensation styling still wins.
   */
  readonly ownerColor?: (path: readonly string[]) => string | undefined;
  /** v0.10.2 per-step latencies keyed by walk stepIndex — the sequence ruler
   *  (cumulative elapsed) + per-hop arrow labels (own latency). */
  readonly stepLatencies?: ReadonlyMap<number, StepLatency>;
  /** Live conformance: walk stepIndexes observed to run out of order — marked
   *  with a violation ring on their block cell. */
  readonly reorderedSteps?: ReadonlySet<number>;
  /** WYSIWYG step editing — the ⋯ affordance on each block cell. Read-only when
   *  absent / `canEdit` false. */
  readonly stepEdit?: ProcessStepEditApi;
  /** SHARED multi-select model (P5 #34), lifted to ProcessMode so the flow /
   *  sequence board and the tree drive ONE selection. In EDIT MODE a step's
   *  plain / ⌘ / Shift click routes here (select the step to edit + highlight)
   *  instead of the scrubber. Absent ⇒ view-mode behaviour (click = scrubber). */
  readonly selection?: ProcessSelection;
  /** Document-order axis a Shift-range walks — `orderedStepIds(walk)` lifted
   *  from ProcessMode so tree + swimlane range over the same list. */
  readonly stepOrder?: readonly NodeId[];
}

export function SwimlaneView({
  layout,
  walkResult,
  walkerState,
  activeStepIndex,
  onSelectStepIndex,
  onBranchSelect,
  onSubprocessToggle,
  onSelectModule,
  resolveStepEdgeStyle,
  autoPlay = false,
  onAutoAdvance,
  ownerColor,
  stepLatencies,
  reorderedSteps,
  stepEdit,
  selection,
  stepOrder,
}: SwimlaneViewProps) {
  const variant = layout.variant;
  // In edit mode a step click SELECTS it for editing (shared selection, P5 #34)
  // rather than driving the playback scrubber. View mode keeps the scrubber.
  const editSelecting = stepEdit?.canEdit === true && !!selection;
  const nLanes = Math.max(layout.lanes.length, 1);
  // "Colour by" lane tint: module lanes key into the unified coloring layer
  // (actors have no module — they stay neutral).
  const laneColorById = useMemo(() => buildLaneColorById(layout.lanes, ownerColor), [layout.lanes, ownerColor]);
  // Explicit per-frame branch picks (if/switch/try). A frame in this map
  // shows ONLY its chosen branch as the live path (triangles stay); a frame
  // absent from it shows all branches. Clicking a branch arrow/label selects;
  // clicking the gateway triangle clears (→ show all again).
  const branchSel = walkerState.branchSelection;
  // Chain / gateway / hidden-row topology — the SAME pure pass scene's
  // `swimlaneGeometry` and the export composer consume, so the live view and
  // the headless backend read ONE source of truth. `laneIndex`, `flowChain`,
  // `gateways`, `gatewaySkip` and the hidden-row set are all derived off it.
  const topo = useMemo(
    () =>
      swimlaneTopology(layout, {
        isCommentRow: (r) => isCommentRow(r, walkResult),
        branchSelection: branchSel,
      }),
    [layout, walkResult, branchSel],
  );
  const laneIndex = topo.laneIndexOf;
  // Rows hidden by an explicit exclusive branch pick, as a `Set<Row>` for the
  // cell + arc passes (the topology exposes them as row indexes).
  const hiddenRows = useMemo(() => {
    const s = new Set<Row>();
    for (const i of topo.hiddenRowIndexes) {
      const r = layout.rows[i];
      if (r) s.add(r);
    }
    return s;
  }, [topo, layout]);
  const nRows = Math.max(layout.height, 1);
  // Step-travel droplet fires only on a SCRUBBER change (◀ ▶ / auto-play),
  // never on first render or a MANUAL block click — those set `manualSelectRef`
  // so the transition is applied instantly with no animation. `animSeq` bumps
  // only for animated (scrubber) transitions; the droplet keys off it.
  //
  // `highlightStep` is the block shown as active. On a manual change it tracks
  // `activeStepIndex` immediately; on a scrubber change it lags until the
  // droplet REACHES the target (StepDroplets calls `setHighlightStep` on
  // arrival), so the destination lights up only when the ball gets there.
  const manualSelectRef = useRef(false);
  const prevActiveRef = useRef<number | null | undefined>(undefined);
  // The droplet's origin for the NEXT animated transition. Captured here (the
  // effect runs on EVERY step change, so `prev` is always the immediately-prior
  // step) rather than inside StepDroplets, whose effect only runs on animated
  // transitions — a manual jump in between would otherwise leave it stale.
  const dropletFromRef = useRef<number | null>(null);
  const [animSeq, setAnimSeq] = useState(0);
  const [highlightStep, setHighlightStep] = useState<number | null>(activeStepIndex);
  useEffect(() => {
    const prev = prevActiveRef.current;
    prevActiveRef.current = activeStepIndex;
    const animate = shouldAnimateTransition(prev, activeStepIndex, manualSelectRef.current, variant === "flow");
    manualSelectRef.current = false;
    if (animate) {
      dropletFromRef.current = prev ?? null;
      setAnimSeq((s) => s + 1); // scrubber: animate; highlight lags to arrival
    } else {
      setHighlightStep(activeStepIndex); // instant
    }
  }, [activeStepIndex, variant]);
  /** Mark the next step change as a manual in-diagram click (no animation). */
  const selectStepManual = (i: number | null) => {
    manualSelectRef.current = true;
    onSelectStepIndex(i);
  };
  // A block/comment cell click. EDIT MODE (shared selection present): plain
  // replaces, ⌘ toggles, Shift ranges over `stepOrder` — feeding the same
  // selection Delete/Copy/Paste + the floating toolbar operate on. VIEW MODE:
  // the scrubber, re-clicking the active step clears it (unchanged).
  const onStepClick = (
    stepNodeId: NodeId | undefined,
    stepIndex: number,
    e: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean },
  ): void => {
    if (editSelecting && stepNodeId) {
      selection!.onRowClick(stepNodeId, clickModifiers(e), stepOrder ?? []);
      return;
    }
    selectStepManual(stepIndex === activeStepIndex ? null : stepIndex);
  };
  // Auto-play cadence: once the current step has SETTLED (droplet reached it,
  // or instant for the start/sequence), dwell, then advance. This waits for the
  // animation to finish rather than racing a fixed timer.
  useEffect(() => {
    if (!autoPlay || !onAutoAdvance) return;
    // Mid-flight (droplet still travelling to the active step) → wait.
    if (activeStepIndex != null && highlightStep !== activeStepIndex) return;
    const t = window.setTimeout(() => onAutoAdvance(), AUTO_DWELL_MS);
    return () => window.clearTimeout(t);
  }, [autoPlay, activeStepIndex, highlightStep, onAutoAdvance]);

  const laneLabel = useMemo(() => makeLaneLabel(layout.lanes), [layout]);
  // Occupied callee lanes per row. Lets a same-row arrow decide whether it
  // must arc "over the top" (a block sits between source and target) vs run
  // straight, and lets the entry dot avoid a lane that a serpentine merge
  // dropped a block into on its row.
  const rowOccupancy = useMemo(() => makeRowOccupancy(layout.rows, laneIndex), [layout, laneIndex]);
  const laneOccupied = rowOccupancy.laneOccupied;

  // Temporal chain for Flow's control-flow arrows. Operates on FLOW
  // NODES — real steps plus `stepIndex: null` rows that still take part
  // in the flow: subprocess `do` headers and terminators (so neither is
  // left orphaned). Each node links to its predecessor, skipping:
  //   1. Comments (notes with no actor) — annotations, not flow.
  //   2. Unselected alternatives — the main chain runs through the
  //      SELECTED branch only; a node after a branched frame chains from
  //      the selected branch's tail.
  //   3. Sibling branches — a node in branch A never chains from branch B.
  // Branch identity comes from row-level fields (stamped by core for
  // every row, including null ones), so terminators / do-headers get
  // placed in the right branch even though they have no walker step.
  const flowChain = topo.flowChain;

  // Gateway model (flow): each branched frame is bracketed by a split and a
  // merge diamond row. The chain reads fork → split → branch heads, and
  // branch tails → merge → continuation. Computes the diamond rows, each
  // branch's head/tail flow node, and the fork/continuation around the frame.
  const gateways = topo.gateways;
  // Branch heads + continuations are drawn by the gateway pass (split→head,
  // merge→continuation), so the regular flow-arrow pass skips them.
  const gatewaySkip = topo.gatewaySkip;

  // Unified flow graph (topology only): typed nodes — steps (`s<idx>`),
  // non-step flow nodes (`r<rowIdx>`: terminators / subprocess headers), and
  // gateway diamonds (`split:<fid>` / `merge:<fid>`) — plus directed edges in
  // execution order. Each arrow is tagged with its edge id; the step-travel
  // droplet BFS-walks this graph from the previous active step to the current
  // one, so it flows A→split→B and tail→merge→split→head through the markers
  // (not a per-arrow heuristic). Geometry stays in the renderers — the graph
  // is topology; the droplet reads each edge's measured `d` from the DOM.
  const flowGraph = useMemo(() => {
    if (variant !== "flow") return emptyFlowGraph();
    // Walker nodeId → flow-graph node key. Steps key by their dense index
    // (`s<idx>`); terminator / subprocess-header rows by their LAYOUT-ROW index
    // (`r<idx>`) — the same key `buildFlowGraph` derives via idxOfRow, and the
    // only way to key the compensation rows it excludes from `flowChain`.
    const nodeKeyByNodeId = new Map<string, string>();
    for (const s of walkResult?.steps ?? []) nodeKeyByNodeId.set(s.nodeId, `s${s.stepIndex}`);
    layout.rows.forEach((r, idx) => {
      if (r.terminator) nodeKeyByNodeId.set(r.terminator.nodeId, `r${idx}`);
      if (r.subprocessHeader) nodeKeyByNodeId.set(r.subprocessHeader.nodeId, `r${idx}`);
    });
    return buildFlowGraph(flowChain, gateways, gatewaySkip, walkResult?.collapsedGroups, {
      compensation: walkResult?.compensation,
      reentries: walkResult?.reentries,
      nodeKeyByNodeId,
    });
  }, [variant, flowChain, gateways, gatewaySkip, walkResult, layout]);

  // Flow-graph node key → its Row, so the aux-edge pass can resolve a `drop` /
  // `comp` / `reentry` endpoint (a node KEY) back to geometry. Steps key by
  // `s<stepIndex>`; terminator / do-header rows by `r<layout-row idx>` — the same
  // keys `flowGraph` / nodeKeyByNodeId use.
  const rowByNodeKey = useMemo(() => computeRowByNodeKey(layout), [layout]);

  // Row indexes that are START triggers (a leading inbound `await`) — the same
  // `walk.triggers` membership the per-step `isTrigger` reads below, lifted to a
  // set so the geometry pass can budget the `▶` marker into line 0's wrap.
  // Mirrors viewer-core's `swimlaneExportOptions.triggerRowIndexes` so live and
  // headless size trigger blocks identically.
  const triggerRowIndexes = useMemo(() => computeTriggerRowIndexes(layout, walkResult), [layout, walkResult]);

  // Pure pixel geometry — the same `swimlaneGeometry` pass the headless SVG
  // export runs (arrow bands, frame padding, text wrap via Inter metrics, block
  // rects). The label/comment resolvers thread the SAME walk-derived strings
  // the cells render, so the wrap the geometry computes IS what gets painted.
  // Row indexes that are MID-FLOW `await` waits — an `await` step that is NOT a
  // leading start trigger (PROCESS.md §6). Rendered with a timer/clock marker,
  // distinct from a plain note and from the ▶ start trigger; budgeted like the
  // trigger marker in the geometry so its first line wraps identically.
  const waitRowIndexes = useMemo(() => computeWaitRowIndexes(layout, walkResult), [layout, walkResult]);

  const sceneGeom = useMemo(
    () =>
      swimlaneGeometry(layout, metricsMeasure, {
        blockLabel: (r) => labelForStep(r, walkResult, variant, laneLabel),
        isCommentRow: (r) => isCommentRow(r, walkResult),
        branchSelection: branchSel,
        triggerRowIndexes,
        waitRowIndexes,
      }),
    [layout, walkResult, variant, laneLabel, branchSel, triggerRowIndexes, waitRowIndexes],
  );
  const laneLabelLines = useMemo(
    () => new Map(sceneGeom.lanes.map((l) => [l.id, l.labelLines] as const)),
    [sceneGeom],
  );
  const rowTopPad = useCallback((y: number) => sceneGeom.rowTopPads[y] ?? 8, [sceneGeom]);
  const rowBottomPad = useCallback((y: number) => sceneGeom.rowBottomPads[y] ?? 8, [sceneGeom]);

  // Grid-relative view of the pure geometry, in the committed RowGeometry
  // shape the overlay consumes. The blocks map mirrors the committed DOM
  // registration set exactly: unanchored prose cells never registered a ref,
  // and the sequence do-band carried none (flow do / lane −1 terminators DID —
  // committed quirk, preserved by the geometry pass).
  const overlayRef = useRef<SVGSVGElement | null>(null);
  const geom: RowGeometry = useMemo(() => {
    const H = sceneGeom.headerH;
    const blocks = new Map<string, { top: number; bottom: number }>();
    for (const [key, b] of sceneGeom.blocks) {
      if (b.kind === "prose") continue;
      if (b.kind === "do" && sceneGeom.variant === "sequence") continue;
      blocks.set(key, { top: b.rect.y - H, bottom: b.rect.y + b.rect.h - H });
    }
    return {
      tops: sceneGeom.rowTops.map((t) => t - H),
      heights: sceneGeom.rowHeights,
      total: sceneGeom.gridHeight,
      blocks,
    };
  }, [sceneGeom]);

  const width = nLanes * LANE_WIDTH;
  // Frame rects, nesting depths, and band-start top-extras come SOLVED from
  // the pure geometry pass — one derivation shared with the headless export
  // (pinned by geometry-parity), replacing the local computeFrameDepths /
  // frameTopExtras / frameRect trio.
  const frameGeomById = useMemo(
    () => new Map(sceneGeom.frames.map((fg) => [fg.id, fg])),
    [sceneGeom],
  );
  const depthOf = useCallback(
    (id: string) => frameGeomById.get(id)?.depth ?? 0,
    [frameGeomById],
  );
  const sortedFrames = useMemo(() => {
    return [...layout.frames].sort((a, b) => {
      const da = depthOf(a.id);
      const db = depthOf(b.id);
      if (da !== db) return da - db;
      if (a.yStart !== b.yStart) return a.yStart - b.yStart;
      return a.id.localeCompare(b.id);
    });
  }, [layout, depthOf]);

  // Global crossing-minimised arrow routing. We collect every cross-column
  // arrow whose horizontal bridge Y is FREE to choose (main flow arrows +
  // decision branch arrows), plus the FIXED segments everything must dodge
  // (same-row arcs/horizontals, same-column verticals, entry stubs,
  // decision arcs), then jointly minimise total crossings (see
  // optimizeBridges). Output: bridge Y per arrow, keyed by row index (flow)
  // / `frameId:label` (decision).
  const routing = useMemo(
    () =>
      variant === "flow"
        ? swimlaneRouting(layout, sceneGeom, topo)
        : { flowMap: new Map<number, number>(), gatewayMap: new Map<string, number>() },
    [layout, variant, sceneGeom, topo],
  );

  // Flow-variant aux edges (saga drop / reverse compensation chain / `go`
  // back-arcs). The live view resolves the walk/flow-graph endpoints to the
  // row-index `SwimlaneAuxEdge[]` scene's `swimlaneFlowAux` takes, keeps the
  // `from`-key sidecar for the re-entry-comment de-dup, and consumes scene's
  // routed `d` (with its `midX`/`midY` label anchor) directly — no re-route.
  const flowAux = useMemo<{ paths: SwimlaneAuxPath[]; fromById: ReadonlyMap<string, string> }>(() => {
    if (variant !== "flow") return { paths: [], fromById: new Map() };
    const { edges, fromById } = resolveFlowAuxEdges(layout, flowGraph, rowByNodeKey);
    return { paths: swimlaneFlowAux(layout, sceneGeom, topo, edges), fromById };
  }, [layout, variant, sceneGeom, topo, flowGraph, rowByNodeKey]);

  // Sequence `go` re-entry arcs (PROCESS.md §7): a right-side purple arc from
  // the `go` marker block back to its `as`-named target block — the sequence
  // analogue of flow's aux re-entry edges. An arc is dropped (the comment then
  // falls back to the block-above label) when the target is unresolved, either
  // row is missing (e.g. inside a collapsed `do`), or either row sits in a
  // hidden band.
  const seqReentryArcs = useMemo<{ arcs: SwimlaneSeqArc[]; fromById: ReadonlyMap<string, string> }>(() => {
    if (variant !== "sequence") return { arcs: [], fromById: new Map() };
    const { edges, fromById } = resolveSeqReentryEdges(layout, walkResult, hiddenRows);
    return { arcs: swimlaneSeqArcs(layout, sceneGeom, topo, edges), fromById };
  }, [variant, sceneGeom, topo, walkResult, layout, hiddenRows]);

  // Sequence saga arcs (PROCESS.md §8): the reverse compensation chain (`comp`,
  // solid orange) and each forward step's failure entry into it (`drop`, dashed
  // orange), drawn as right-side arcs — the sequence analogue of flow's aux
  // comp/drop edges. Without them the amber comp step blocks read as ordinary
  // forward messages after the commit; the arcs show the rollback topology.
  const seqCompArcs = useMemo<SwimlaneSeqArc[]>(() => {
    if (variant !== "sequence") return [];
    const edges = resolveSeqCompEdges(layout, walkResult, hiddenRows);
    return swimlaneSeqArcs(layout, sceneGeom, topo, edges).filter((a) => a.kind !== "reentry");
  }, [variant, sceneGeom, topo, walkResult, layout, hiddenRows]);

  // Flow step→step edge geometry, stored ON the graph (keyed by edge id) so
  // the arrows render FROM the graph instead of each FlowArrow recomputing its
  // own path. Same Z-bridge / over-arc / straight logic as before.
  const flowEdgeGeom = useMemo(
    () => (variant === "flow" ? swimlaneFlowEdgeGeom(layout, sceneGeom, topo, routing.flowMap) : new Map<number, string>()),
    [layout, variant, sceneGeom, topo, routing],
  );

  // Gateway geometry, stored ON the graph (one entry per branched frame): the
  // diamond positions, every leg's path + label + pick + edge id, and the
  // collapsed state. `GatewayArrows` is now a pure renderer of this — the
  // bus/gutter/rank/route math lives here, so the graph is the only model.
  const gatewayGeom = useMemo<SwimlaneGwGeom[]>(
    () => (variant === "flow" ? swimlaneGatewayGeom(layout, sceneGeom, topo, routing.gatewayMap) : []),
    [layout, variant, sceneGeom, topo, routing],
  );
  // Only exclusive frames (if/try) are pickable; select/parallel run all arms.
  // GatewayArrows needs this per frame (the leg geometry is kind-agnostic).
  const gwSelectableById = useMemo(
    () => new Map(gateways.map((g) => [g.frame.id, isFrameExclusive(g.frame.kind)] as const)),
    [gateways],
  );

  // Empty-block insert anchors (plan §4b): a gateway block with NO body rows
  // (`parallel {}`, an all-empty `if`/`select`) is EXCLUDED from `topo.gateways`
  // (swimlane-geometry.ts `if (!branches.length) continue`), so no per-step "+"
  // and no gateway geom reach inside it — yet its split/merge rows persist in
  // `layout.rows`. Pair them by frame; when adjacent (nothing between), drop an
  // "add step into" "+" at the row-band midpoint, bound to `insertInto` the
  // block. Geometry from the scene row bands (lane −1 = the diamond band), never
  // measured DOM. Flow variant only (gateway rows are a flow construct).
  const emptyBlockInserts = useMemo(() => {
    if (variant !== "flow") return [] as { fid: string; x: number; y: number; enclosing: InsertEnclosing }[];
    // A `select` body holds only cases → its insert palette is case-shaped
    // (plan §127 contextual-valid). Frame kind is keyed by id in layout.frames;
    // the scene/walk names the select frame `switch` (v0.7 legacy tag).
    const kindByFrame = new Map(layout.frames.map((f) => [f.id, f.kind]));
    const splitBy = new Map<string, Row>();
    const mergeBy = new Map<string, Row>();
    for (const r of layout.rows) {
      if (r.gateway?.kind === "split") splitBy.set(r.gateway.frameId, r);
      else if (r.gateway?.kind === "merge") mergeBy.set(r.gateway.frameId, r);
    }
    const out: { fid: string; x: number; y: number; enclosing: InsertEnclosing }[] = [];
    for (const [fid, split] of splitBy) {
      const merge = mergeBy.get(fid);
      if (!merge) continue;
      const hasBody = layout.rows.some((r) => r.y > split.y && r.y < merge.y && !hiddenRows.has(r));
      if (hasBody) continue; // non-empty → per-step "+" already covers it
      const toI = laneIndex(split.toLaneId);
      if (toI < 0) continue;
      const sCy = blockYBounds(split.y, -1, geom, rowTopPad(split.y), rowBottomPad(split.y)).blockCy;
      const mCy = blockYBounds(merge.y, -1, geom, rowTopPad(merge.y), rowBottomPad(merge.y)).blockCy;
      out.push({
        fid,
        x: toI * LANE_WIDTH + LANE_WIDTH / 2,
        y: (sCy + mCy) / 2,
        enclosing: kindByFrame.get(fid) === "switch" ? "select" : "flow",
      });
    }
    return out;
  }, [variant, layout, hiddenRows, laneIndex, geom, rowTopPad, rowBottomPad]);

  // P2 structured drag: reorder a flow step by dragging its handle. Candidates
  // are the draggable step rows as scene-geometry bands (rowTops/rowHeights —
  // NO DOM-measured layout); `toLocalY` projects the pointer into that space
  // through the board's CSS zoom via the overlay's own client rect (pointer
  // hit-testing, not a layout decision). The drop commits via `moveStep`.
  const dragRows = useCallback((): DragRow[] => {
    if (variant !== "flow" || !walkResult) return [];
    const out: DragRow[] = [];
    for (const r of layout.rows) {
      if (r.stepIndex === null || hiddenRows.has(r) || r.gateway || r.terminator) continue;
      const nodeId = walkResult.byIndex.get(r.stepIndex)?.nodeId;
      if (!nodeId) continue;
      out.push({ nodeId: String(nodeId), top: sceneGeom.rowTops[r.y] ?? 0, height: sceneGeom.rowHeights[r.y] ?? 0 });
    }
    return out;
  }, [variant, walkResult, layout, hiddenRows, sceneGeom]);

  // Empty block bodies are unambiguous reparent (drop-into) zones — a step
  // dropped here prepends into the block (moveStepInto). Same split/merge frame
  // math as the empty-block insert, as rects in overlay space.
  const blockZones = useCallback((): BlockZone[] => {
    if (variant !== "flow") return [];
    const splitBy = new Map<string, Row>();
    const mergeBy = new Map<string, Row>();
    for (const r of layout.rows) {
      if (r.gateway?.kind === "split") splitBy.set(r.gateway.frameId, r);
      else if (r.gateway?.kind === "merge") mergeBy.set(r.gateway.frameId, r);
    }
    const out: BlockZone[] = [];
    for (const [fid, split] of splitBy) {
      const merge = mergeBy.get(fid);
      if (!merge) continue;
      if (layout.rows.some((r) => r.y > split.y && r.y < merge.y && !hiddenRows.has(r))) continue;
      const toI = laneIndex(split.toLaneId);
      if (toI < 0) continue;
      const top = blockYBounds(split.y, -1, geom, rowTopPad(split.y), rowBottomPad(split.y)).blockCy;
      const bottom = blockYBounds(merge.y, -1, geom, rowTopPad(merge.y), rowBottomPad(merge.y)).blockCy;
      out.push({ blockNodeId: String(fid), top, bottom, left: toI * LANE_WIDTH, right: (toI + 1) * LANE_WIDTH });
    }
    return out;
  }, [variant, layout, hiddenRows, laneIndex, geom, rowTopPad, rowBottomPad]);

  const toLocalY = useCallback((clientY: number): number => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || rect.height === 0) return clientY;
    return (clientY - rect.top) * (sceneGeom.height / rect.height);
  }, [sceneGeom.height]);
  const toLocalX = useCallback((clientX: number): number => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return clientX;
    return (clientX - rect.left) * (width / rect.width);
  }, [width]);

  // Block → its descendant leaf nodeIds, so a WHOLE-BLOCK drag (grabbing a
  // gateway diamond, P2 #19) never anchors onto a row inside its own body — the
  // dragRows anchors include the block's children, which must be excluded. Keyed
  // by the block's walker nodeId (the frame id the diamond carries).
  const blockDescendants = useMemo(
    () => treeDragModel(walkResult).descendants,
    [walkResult],
  );

  const drag = useStepDrag({
    resolve: (clientX, clientY, dragged) => {
      const px = toLocalX(clientX);
      const py = toLocalY(clientY);
      // Reparent-into wins over reorder when the pointer is inside a block body.
      const block = resolveBlockDrop(blockZones(), px, py, dragged);
      if (block) return { kind: "into", blockNodeId: block };
      // Dragging a whole block: drop its own descendant leaf rows as anchors
      // (can't reorder a block relative to a row it carries).
      const carried = blockDescendants.get(dragged as NodeId);
      const rows = carried ? dragRows().filter((r) => !carried.has(r.nodeId as NodeId)) : dragRows();
      const v = resolveVerticalDrop(rows, py, dragged);
      return v ? { kind: "reorder", anchorNodeId: v.anchorNodeId, position: v.position } : null;
    },
    commit: (dragged, t) => {
      if (t.kind === "into") stepEdit?.moveStepInto(dragged as unknown as NodeId, t.blockNodeId as unknown as NodeId);
      else stepEdit?.moveStep(dragged as unknown as NodeId, t.anchorNodeId as unknown as NodeId, t.position);
    },
  });

  // The drop indicator: an insertion LINE for a reorder, a highlighted RECT for
  // a reparent-into-block.
  const dropIndicator = useMemo((): { kind: "line"; y: number } | { kind: "rect"; z: BlockZone } | null => {
    const t = drag.target;
    if (!t) return null;
    if (t.kind === "reorder") {
      const anchor = dragRows().find((b) => b.nodeId === t.anchorNodeId);
      return anchor ? { kind: "line", y: t.position === "before" ? anchor.top : anchor.top + anchor.height } : null;
    }
    const z = blockZones().find((b) => b.blockNodeId === t.blockNodeId);
    return z ? { kind: "rect", z } : null;
  }, [drag.target, dragRows, blockZones]);

  return (
    // `safe center`: centre the board when it fits, but fall back to start
    // (left) alignment when it's wider than the viewport — plain `center` would
    // overflow the left edge out of the scroll range, stranding the leftmost
    // lanes (Chrome can't scroll to them). Wide diagrams stay fully reachable.
    <div className="w-full h-full overflow-auto bg-canvas flex" style={{ justifyContent: "safe center" }}>
      {/* Presentation-only magnification (CSS `zoom`, not `transform: scale` —
          the latter would break the sticky lane header). Wraps the ruler +
          board so both scale together; the geometry underneath stays at
          scale 1. */}
      <div className="flex" style={{ zoom: PROCESS_VIEW_ZOOM }}>
      {/* v0.10.2 latency ruler — cumulative elapsed per step (sequence only).
          A left gutter reading the SAME scene row positions; disturbs no
          existing geometry (a sibling column, not a grid change). */}
      {variant === "sequence" &&
        stepLatencies &&
        [...stepLatencies.values()].some((s) => !!s.label && s.label !== "???") && (
        <div className="relative shrink-0" style={{ width: 60, minHeight: sceneGeom.height }}>
          <div
            className="absolute left-0 right-0 flex items-end justify-end pr-1 pb-1 text-[9px] uppercase tracking-wide text-slate-500"
            style={{ top: 0, height: sceneGeom.headerH }}
          >
            elapsed
          </div>
          {layout.rows.map((r, i) => {
            if (r.stepIndex === null || hiddenRows.has(r)) return null;
            const sl = stepLatencies.get(r.stepIndex);
            if (!sl) return null;
            return (
              <div
                key={`ruler-${i}`}
                className="absolute left-0 right-0 flex items-center justify-end pr-1 text-[9px] tabular-nums text-slate-400"
                style={{ top: sceneGeom.rowTops[r.y], height: sceneGeom.rowHeights[r.y] }}
                title="Cumulative elapsed (linear reading of the shown steps)"
              >
                {sl.cumLabel}
              </div>
            );
          })}
        </div>
      )}
      <div
        className="relative shrink-0 group/swim"
        style={{ width: sceneGeom.containerWidth, minHeight: sceneGeom.height }}
      >
        <LaneHeaders
          lanes={layout.lanes}
          labelLines={laneLabelLines}
          onSelectModule={onSelectModule}
          laneColor={laneColorById}
        />
        <div
          className="relative"
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${nLanes}, ${LANE_WIDTH}px)`,
            // Explicit heights from the pure geometry — the browser no longer
            // sizes rows (cells render pre-wrapped lines at fixed heights).
            gridTemplateRows: sceneGeom.rowHeights.map((h) => `${h}px`).join(" "),
          }}
        >
          {layout.rows.map((r, i) => {
            // Hidden = an unselected branch of an explicitly-selected frame.
            if (hiddenRows.has(r)) return null;
            if (r.stepIndex === null) {
              // Gateway diamonds are drawn in the SVG overlay (at splitX/
              // mergeX), so the row reserves height but renders no cell.
              if (r.gateway) return null;
              if (r.subprocessHeader) {
                const sh = r.subprocessHeader;
                // A COLLAPSED `do` highlights when the active step is inside
                // its hidden body — the droplet travels to the `do` node.
                const range = !sh.expanded ? walkResult?.collapsedGroups.get(sh.nodeId) : undefined;
                const doActive = !!range && highlightStep != null && highlightStep >= range.startIndex && highlightStep <= range.endIndex;
                return (
                  <SubprocessBand
                    key={`do-${i}`}
                    row={r}
                    variant={variant}
                    active={doActive}
                    laneIndex={laneIndex}
                    topPad={rowTopPad(r.y)}
                    bottomPad={rowBottomPad(r.y)}
                    blockGeom={sceneGeom.blocks.get(
                      `${r.y}:${variant === "sequence" ? -1 : laneIndex(r.toLaneId)}`,
                    )}
                    onClick={() => onSubprocessToggle(sh.nodeId, !sh.expanded)}
                  />
                );
              }
              return (
                <TerminatorCell
                  key={`term-${i}`}
                  row={r}
                  laneIndex={laneIndex}
                  nLanes={nLanes}
                  topPad={rowTopPad(r.y)}
                  bottomPad={rowBottomPad(r.y)}
                  blockGeom={sceneGeom.blocks.get(`${r.y}:${laneIndex(r.toLaneId)}`)}
                  stepEdit={stepEdit}
                />
              );
            }
            const step = walkResult?.byIndex.get(r.stepIndex);
            const isNote = !!step?.noteText;
            const isComment = isNote && step!.from.length === 0;
            const isTrigger = !!step && walkResult?.triggers?.includes(step.nodeId) === true;
            const isWait = waitRowIndexes.has(i);
            const active = r.stepIndex === highlightStep;
            const edgeStyle = resolveStepEdgeStyle
              ? resolveStepEdgeStyle(r.stepIndex)
              : undefined;
            const label = labelForStep(r, walkResult, variant, laneLabel);
            const branchSelected = step?.modifiers?.branchSelected ?? true;
            if (isComment) {
              // Flow renders ownerless notes too — v0.9.0 §2.6 flow prose (and
              // the ownerless synthetic timer await) are positional rows, not
              // sequence-only annotations.
              return (
                <CommentCell
                  key={`cmt-${i}`}
                  row={r}
                  text={label.primary}
                  trigger={isTrigger}
                  wait={isWait}
                  active={active}
                  laneIndex={laneIndex}
                  topPad={rowTopPad(r.y)}
                  bottomPad={rowBottomPad(r.y)}
                  blockGeom={sceneGeom.blocks.get(
                    `${r.y}:${laneIndex(r.toLaneId) >= 0 ? laneIndex(r.toLaneId) : -1}`,
                  )}
                  onClick={(e) => {
                    if (r.stepIndex !== null) onStepClick(step?.nodeId, r.stepIndex, e);
                  }}
                />
              );
            }
            const isSelected = selection?.has(step?.nodeId as NodeId) === true && step?.nodeId !== undefined;
            return (
              <BlockCell
                key={`block-${i}`}
                row={r}
                variant={variant}
                label={label}
                isNote={isNote}
                trigger={isTrigger}
                wait={isWait}
                active={active}
                reordered={r.stepIndex !== null && !!reorderedSteps?.has(r.stepIndex)}
                isEntry={r.stepIndex === 0}
                edgeStyle={edgeStyle}
                laneIndex={laneIndex}
                topPad={rowTopPad(r.y)}
                bottomPad={rowBottomPad(r.y)}
                branchSelected={branchSelected}
                branchLabel={step?.modifiers?.branchLabel}
                reentryTarget={step?.reentryTarget}
                laneOwnerColor={laneColorById.get(r.toLaneId)}
                blockGeom={sceneGeom.blocks.get(`${r.y}:${laneIndex(r.toLaneId)}`)}
                selected={isSelected}
                onClick={(e) => {
                  if (r.stepIndex !== null) onStepClick(step?.nodeId, r.stepIndex, e);
                }}
                stepEdit={stepEdit}
                stepNodeId={step?.nodeId}
                stepIndex={r.stepIndex}
                stepFrom={!isNote && step ? step.from.join(".") : undefined}
                stepTo={!isNote && step ? step.to.join(".") : undefined}
                stepModifiers={
                  !isNote && step
                    ? { ...(step.asName ? { asName: step.asName } : {}), ...(step.comment ? { comment: step.comment } : {}) }
                    : undefined
                }
                onDragHandleDown={variant === "flow" ? (nodeId, e) => drag.onHandleDown(String(nodeId), e) : undefined}
                onBodyDragDown={variant === "flow" ? (nodeId, e) => drag.onBodyDown(String(nodeId), e) : undefined}
                dragging={!!step?.nodeId && drag.draggingNodeId === String(step.nodeId)}
              />
            );
          })}
        </div>
        {/* SVG overlay: lane separators, frames, arrows. Pointer events
            disabled by default; branch tabs re-enable them locally. */}
        <svg
          ref={overlayRef}
          className="absolute top-0 left-0 pointer-events-none"
          width={width}
          height={sceneGeom.height}
          style={{ overflow: "visible" }}
        >
          <defs>
            <marker id="sw-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="12" markerHeight="12" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#94a3b8" />
            </marker>
            <marker id="sw-arrow-active" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="12" markerHeight="12" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#38bdf8" />
            </marker>
            <marker id="sw-flow-tick" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="10" markerHeight="10" orient="auto-start-reverse">
              <path d="M 0 1 L 10 5 L 0 9 z" fill="#475569" />
            </marker>
            <marker id="sw-flow-tick-err" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="10" markerHeight="10" orient="auto-start-reverse">
              <path d="M 0 1 L 10 5 L 0 9 z" fill="#ef4444" />
            </marker>
            {/* Compensation (drop / reverse chain) — amber; re-entry (`go`) — purple. */}
            <marker id="sw-comp-tick" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="10" markerHeight="10" orient="auto-start-reverse">
              <path d="M 0 1 L 10 5 L 0 9 z" fill="#f59e0b" />
            </marker>
            <marker id="sw-reentry-tick" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="11" markerHeight="11" orient="auto-start-reverse">
              <path d="M 0 1 L 10 5 L 0 9 z" fill="#c4b5fd" />
            </marker>
            {/* Soft halo for the step-travel droplet. */}
            <filter id="sw-droplet-glow" x="-150%" y="-150%" width="400%" height="400%">
              <feGaussianBlur stdDeviation="2.2" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* Drop indicator — an insertion line for a reorder, a highlighted
              rect for a reparent-into-block (P2 #16/#17). */}
          {dropIndicator?.kind === "line" && (
            <line
              x1={0}
              x2={width}
              y1={dropIndicator.y}
              y2={dropIndicator.y}
              stroke="#38bdf8"
              strokeWidth={2.5}
              strokeDasharray="7 3"
              data-drop-line=""
              pointerEvents="none"
            />
          )}
          {dropIndicator?.kind === "rect" && (
            <rect
              x={dropIndicator.z.left + 2}
              y={dropIndicator.z.top + 2}
              width={Math.max(0, dropIndicator.z.right - dropIndicator.z.left - 4)}
              height={Math.max(0, dropIndicator.z.bottom - dropIndicator.z.top - 4)}
              fill="#38bdf8"
              fillOpacity={0.12}
              stroke="#38bdf8"
              strokeWidth={2}
              strokeDasharray="6 3"
              rx={6}
              data-drop-into=""
              pointerEvents="none"
            />
          )}
          <LaneSeparators count={nLanes} bottom={LANE_HEADER_H + geom.total} />
          {/* `do` frames are drawn by SubprocessSection/Rail below, not as
              generic frame rects. */}
          {variant === "sequence" && sortedFrames
            .filter((f) => f.kind !== "do")
            .map((f) => (
              <FrameGroup
                key={f.id}
                frame={f}
                rect={frameGeomById.get(f.id)?.rect ?? { x: 0, y: 0, w: 0, h: 0 }}
                pickedLabel={isFrameExclusive(f.kind) ? branchSel.get(f.id) : undefined}
                geom={geom}
                onBranchSelect={onBranchSelect}
              />
            ))}
          {/* Sequence: an expanded `do` is one full-width section box
              wrapping its header band + body (supports nesting via inset). */}
          {variant === "sequence" && sortedFrames
            .filter((f) => f.kind === "do" && !f.collapsed)
            .map((f) => (
              <SubprocessSection
                key={`sec-${f.id}`}
                frame={f}
                depth={depthOf(f.id)}
                nLanes={nLanes}
                geom={geom}
              />
            ))}
          {/* Flow draws no frame rects, but an expanded `do` still needs a
              visible extent — a left bracket spanning its header + body. */}
          {variant === "flow" && sortedFrames
            .filter((f) => f.kind === "do" && !f.collapsed)
            .map((f) => (
              <SubprocessRail key={`rail-${f.id}`} frame={f} rows={layout.rows} laneIndex={laneIndex} geom={geom} />
            ))}
          {/* Flow loop markers: an `each` frame draws a violet dashed bracket
              + badge around its iterated body (no frame rects in flow). */}
          {variant === "flow" && sortedFrames
            .filter((f) => f.kind === "each")
            .map((f) => (
              <EachLoopRail key={`each-${f.id}`} frame={f} rows={layout.rows} laneIndex={laneIndex} geom={geom} rowTopPad={rowTopPad} />
            ))}
          {variant === "flow" && flowChain.nodes.map(({ row: r, idx }) => {
            // Branch heads + continuations are drawn by the gateway pass
            // (fork → split → heads, tails → merge → continuation).
            if (gatewaySkip.has(r)) return null;
            const prev = flowChain.prevOf.get(idx) ?? null;
            const isStep = r.stepIndex !== null;
            const edgeStyle = isStep && resolveStepEdgeStyle
              ? resolveStepEdgeStyle(r.stepIndex!)
              : undefined;
            return (
              <FlowArrow
                key={`flow-${idx}`}
                row={r}
                prevRow={prev}
                isEntry={isStep && isFirstRealStep(r.stepIndex!, walkResult)}
                active={isStep && r.stepIndex === highlightStep}
                edgeStyle={edgeStyle}
                geom={geom}
                laneIndex={laneIndex}
                rowTopPad={rowTopPad}
                rowBottomPad={rowBottomPad}
                laneOccupied={laneOccupied}
                edgeId={flowGraph.flowEdgeIdByNodeIdx.get(idx)}
                d={flowEdgeGeom.get(idx)}
              />
            );
          })}
          {variant === "flow" && gatewayGeom.map((gw) => (
            <GatewayArrows
              key={`gw-${gw.frameId}`}
              gw={gw}
              edgeIds={flowGraph.gatewayEdgeIds.get(gw.frameId)}
              selectable={gwSelectableById.get(gw.frameId) ?? false}
              selected={branchSel.has(gw.frameId)}
              onBranchSelect={onBranchSelect}
              // Whole-block drag (P2 #19): grabbing the split diamond relocates the
              // ENTIRE block (moveStep on the frame's nodeId). Edit mode only.
              onBlockDragDown={stepEdit?.canEdit ? (e) => drag.onHandleDown(String(gw.frameId), e) : undefined}
              dragging={drag.draggingNodeId === String(gw.frameId)}
            />
          ))}
          {/* WYSIWYG insert affordance (plan §4a): one "+" per real step, at the
              step block's bottom-centre — inserting "after this step" is the gap
              between it and the next. Both flow AND sequence — BlockCell places a
              step at `gridColumn: toI+1` regardless of variant (`void variant`)
              and `blockYBounds` reads the variant-agnostic scene geom, so the
              same x/y is correct for either. Hover-revealed via the board's
              `group/swim`; present only in edit mode (`stepEdit.canEdit`). */}
          {stepEdit?.canEdit && layout.rows.map((r, i) => {
            if (r.stepIndex === null || hiddenRows.has(r)) return null;
            const step = walkResult?.byIndex.get(r.stepIndex);
            if (!step || step.noteText !== undefined) return null; // real calls only
            const toI = laneIndex(r.toLaneId);
            if (toI < 0) return null;
            const { blockBottom } = blockYBounds(r.y, toI, geom, rowTopPad(r.y), rowBottomPad(r.y));
            const nodeId = step.nodeId;
            // The "+" rides in the gap BELOW the step. Flow: a short vertical stub
            // along the lane connector, hover zone = that lane's gap. Sequence: a
            // horizontal rule across the whole row (Confluence "insert row"), hover
            // zone = the full-width gap band.
            const seq = variant === "sequence";
            const boardW = nLanes * LANE_WIDTH;
            const laneCx = toI * LANE_WIDTH + LANE_WIDTH / 2;
            const gapY = blockBottom + INSERT_GAP / 2;
            const anchorX = seq ? boardW / 2 : laneCx;
            const line = seq
              ? { x1: INSERT_INSET, y1: gapY, x2: boardW - INSERT_INSET, y2: gapY }
              : { x1: laneCx, y1: blockBottom + 1, x2: laneCx, y2: blockBottom + INSERT_GAP - 1 };
            const hover = seq
              ? { x: 0, y: blockBottom, w: boardW, h: INSERT_GAP }
              : { x: toI * LANE_WIDTH + LANE_WIDTH * 0.12, y: blockBottom, w: LANE_WIDTH * 0.76, h: INSERT_GAP };
            return (
              <ConnectorInsert
                key={`ins-${i}`}
                kind="after-step"
                x={anchorX}
                y={gapY}
                hover={hover}
                line={line}
                onSubmit={(text) => stepEdit.addStepAfter(nodeId, text)}
              />
            );
          })}
          {/* Empty-block insert (plan §4b): the "add step into" "+" for blocks
              with no body rows — anchors precomputed in `emptyBlockInserts`. */}
          {stepEdit?.canEdit && emptyBlockInserts.map((a) => (
            <ConnectorInsert
              key={`ins-empty-${a.fid}`}
              kind="into"
              x={a.x}
              y={a.y}
              hover={{ x: a.x - LANE_WIDTH * 0.38, y: a.y - INSERT_GAP / 2, w: LANE_WIDTH * 0.76, h: INSERT_GAP }}
              line={{ x1: a.x, y1: a.y - INSERT_GAP / 2 + 1, x2: a.x, y2: a.y + INSERT_GAP / 2 - 1 }}
              enclosing={a.enclosing}
              onSubmit={(text) => stepEdit.insertInto(a.fid as unknown as NodeId, text)}
            />
          ))}
          {/* "+" AFTER a whole control block (P5 #31): the gaps the per-step map
              can't reach — a block-close → the next block-open, and a TERMINAL
              block → the process end. Anchored just below the merge diamond;
              inserts a sibling after the block via addStepAfter(frameId) (the
              engine lands it after the closing brace). Skipped for a DETACHED
              gateway — every branch terminates, so nothing follows to insert
              before. */}
          {stepEdit?.canEdit && variant === "flow" && gatewayGeom.map((gw) => {
            if (gw.detached) return null;
            const x = gw.mergeX;
            const y = gw.mergeCy + INSERT_GAP;
            return (
              <ConnectorInsert
                key={`ins-after-blk-${gw.frameId}`}
                kind="after-block"
                x={x}
                y={y}
                hover={{ x: x - LANE_WIDTH * 0.38, y: y - INSERT_GAP / 2, w: LANE_WIDTH * 0.76, h: INSERT_GAP }}
                line={{ x1: x, y1: y - INSERT_GAP / 2 + 1, x2: x, y2: y + INSERT_GAP / 2 - 1 }}
                onSubmit={(text) => stepEdit.addStepAfter(String(gw.frameId), text)}
              />
            );
          })}
          {/* "+" at each branch's START — on its diverged entry leg (P5 #32).
              The user's "Pluses live on the horizontal diverged parts": one per
              branch, at the head of its own lane, INSERTing a new first step of
              that branch via insertBefore(headNodeId). The engine promotes a
              one-liner `select` case to a braced body as needed. Skipped when the
              branch head is a nested gateway (no real step to anchor) or hidden. */}
          {stepEdit?.canEdit && variant === "flow" && gateways.flatMap((gw) =>
            gw.branches.map((b) => {
              if (b.head.stepIndex === null || hiddenRows.has(b.head)) return null;
              const step = walkResult?.byIndex.get(b.head.stepIndex);
              if (!step || step.noteText !== undefined) return null;
              const headNodeId = step.nodeId;
              const li = laneIndex(b.head.toLaneId);
              if (li < 0) return null;
              const { blockTop } = blockYBounds(b.head.y, li, geom, rowTopPad(b.head.y), rowBottomPad(b.head.y));
              const x = li * LANE_WIDTH + LANE_WIDTH / 2;
              const y = blockTop - INSERT_GAP / 2;
              return (
                <ConnectorInsert
                  key={`ins-branch-${gw.frame.id}-${b.label}`}
                  kind="branch-start"
                  x={x}
                  y={y}
                  hover={{ x: li * LANE_WIDTH + LANE_WIDTH * 0.12, y: blockTop - INSERT_GAP, w: LANE_WIDTH * 0.76, h: INSERT_GAP }}
                  line={{ x1: x, y1: blockTop - INSERT_GAP + 1, x2: x, y2: blockTop - 1 }}
                  onSubmit={(text) => stepEdit.insertBefore(headNodeId, text)}
                />
              );
            }),
          )}
          {variant === "flow" && flowAux.paths.map((p) => {
            // Backflow (the reverse compensation chain) is SOLID orange — it's the
            // actual rollback path. A `drop` is the per-step "if this step fails"
            // entry — DASHED to read as conditional. `go` re-entry is purple.
            const reentry = p.kind === "reentry";
            return (
              <g key={p.id}>
                <path
                  data-aux-edge={p.kind}
                  d={p.d}
                  fill="none"
                  stroke={reentry ? "#c4b5fd" : "#f59e0b"}
                  strokeWidth={reentry ? 1 : 1.25}
                  strokeDasharray={p.kind === "drop" ? "4 3" : undefined}
                  markerEnd={svgFragmentUrl(reentry ? "sw-reentry-tick" : "sw-comp-tick")}
                  opacity={0.95}
                />
                {/* Freehand `go "note"` comment — the re-entry arc's midpoint label
                    (scene computes the arc midpoint as `midX`/`midY`). */}
                {p.comment && p.midX !== undefined && p.midY !== undefined && (
                  <g style={{ pointerEvents: "none" }}>
                    <title>{p.comment}</title>
                    <text
                      data-aux-comment={p.comment}
                      x={p.midX}
                      y={p.midY - 4}
                      fontSize={8}
                      fill="#c4b5fd"
                      textAnchor="middle"
                      paintOrder="stroke"
                      stroke="#0f172a"
                      strokeWidth={3}
                      strokeLinejoin="round"
                    >
                      {commentLabel(p.comment)}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
          {/* Sequence `go` re-entry arcs — right-side purple back-arcs, the
              freehand `go "note"` comment riding each arc's midpoint. */}
          {variant === "sequence" && seqReentryArcs.arcs.map((a) => (
            <g key={a.id} data-seq-reentry={seqReentryArcs.fromById.get(a.id)} opacity={a.dimmed ? 0.45 : undefined}>
              <path
                d={a.d}
                fill="none"
                stroke="#c4b5fd"
                strokeWidth={1}
                markerEnd={svgFragmentUrl("sw-reentry-tick")}
                opacity={0.95}
              />
              {a.comment && (
                <g style={{ pointerEvents: "none" }}>
                  <title>{a.comment}</title>
                  <text
                    data-aux-comment={a.comment}
                    x={a.commentX}
                    y={a.commentY}
                    fontSize={8}
                    fill="#c4b5fd"
                    textAnchor="start"
                    paintOrder="stroke"
                    stroke="#0f172a"
                    strokeWidth={3}
                    strokeLinejoin="round"
                  >
                    {commentLabel(a.comment)}
                  </text>
                </g>
              )}
            </g>
          ))}
          {/* Sequence saga arcs — the reverse compensation chain (solid orange)
              and forward-step failure drops (dashed orange) off the lifelines. */}
          {variant === "sequence" && seqCompArcs.map((a) => (
            <path
              key={a.id}
              data-seq-aux={a.kind}
              d={a.d}
              fill="none"
              stroke="#f59e0b"
              strokeWidth={1.25}
              strokeDasharray={a.kind === "drop" ? "4 3" : undefined}
              markerEnd={svgFragmentUrl("sw-comp-tick")}
              opacity={a.dimmed ? 0.45 : 0.95}
            />
          ))}
          {variant === "sequence" && layout.rows.map((r, i) => {
            if (r.stepIndex === null) return null;
            // An explicit branch pick hides the frame's other bands — their
            // blocks vanish above, so their arrows must vanish here too.
            if (hiddenRows.has(r)) return null;
            const step = walkResult?.byIndex.get(r.stepIndex);
            if (!step) return null;
            const isComment = step.noteText !== undefined && step.from.length === 0;
            if (isComment) return null;
            if (step.noteText) return null;
            const active = r.stepIndex === highlightStep;
            const edgeStyle = resolveStepEdgeStyle
              ? resolveStepEdgeStyle(r.stepIndex)
              : undefined;
            return (
              <SequenceArrow
                key={`seq-${i}`}
                row={r}
                isEntry={isFirstRealStep(r.stepIndex, walkResult)}
                active={active}
                dimmed={step.modifiers?.branchSelected === false}
                edgeStyle={edgeStyle}
                geom={geom}
                laneIndex={laneIndex}
                rowTopPad={rowTopPad}
                rowBottomPad={rowBottomPad}
              />
            );
          })}
          {/* Trailing call comments (`> Callee "note"`) — one label per commented
              step, in the clear band ABOVE its target block. Rendered here over
              ALL rows (not inside Flow/SequenceArrow) so it survives for rows the
              arrow passes skip: gatewaySkip branch heads + post-merge
              continuations, and comp/`unwind` rows lifted off the forward spine.
              A `go` re-entry's comment normally rides its purple aux arc — but
              that arc only draws in the FLOW variant AND when the target resolves,
              so we render the label above the block whenever the arc won't. */}
          {(() => {
          // `go` rows whose comment IS drawn on an aux re-entry arc — skip those
          // here to avoid a double label. Keyed off the arcs that ACTUALLY render
          // (flow: `flowAux`; sequence: `seqReentryArcs`), NOT the graph edges: an
          // arc is dropped when its target row is hidden (e.g. the `as` step is
          // inside a collapsed `do`), and then the block-above fallback must still
          // fire. Each arc's source `s<idx>` key comes from its `fromById` sidecar.
          const onArcKeys = new Set<string>([
            ...flowAux.paths
              .filter((p) => p.kind === "reentry" && p.comment)
              .map((p) => flowAux.fromById.get(p.id))
              .filter((k): k is string => k !== undefined),
            ...seqReentryArcs.arcs
              .filter((a) => a.comment)
              .map((a) => seqReentryArcs.fromById.get(a.id))
              .filter((k): k is string => k !== undefined),
          ]);
          return layout.rows.map((r, i) => {
            // Commented step rows, plus `do` subprocess-header rows (stepIndex
            // null) whose comment would otherwise show only in the tree.
            if (!r.comment || (r.stepIndex === null && !r.subprocessHeader)) return null;
            if (hiddenRows.has(r)) return null;
            if (r.reentry && r.stepIndex !== null && onArcKeys.has(`s${r.stepIndex}`)) return null;
            const toI = laneIndex(r.toLaneId);
            if (toI < 0) return null;
            const { blockTop } = blockYBounds(r.y, toI, geom, rowTopPad(r.y), rowBottomPad(r.y));
            const mods = r.stepIndex !== null ? walkResult?.byIndex.get(r.stepIndex)?.modifiers : undefined;
            const faded = mods?.branchSelected === false;
            // An unselected branch shows its case label INSIDE the block top
            // (BlockCell) — lift the comment clear of it so they don't collide.
            const clearsBranchLabel = faded && !!mods?.branchLabel;
            return (
              <g key={`cmt-${i}`} style={{ pointerEvents: "none" }} opacity={faded ? 0.45 : 1}>
                <title>{r.comment}</title>
                <text
                  data-flow-comment={r.comment}
                  x={toI * LANE_WIDTH + LANE_WIDTH / 2}
                  y={blockTop - (clearsBranchLabel ? 17 : 6)}
                  fontSize={8}
                  fill="#94a3b8"
                  textAnchor="middle"
                  paintOrder="stroke"
                  stroke="#0f172a"
                  strokeWidth={3}
                  strokeLinejoin="round"
                >
                  {commentLabel(r.comment)}
                </text>
              </g>
            );
          });
          })()}
          {/* v0.10.2 per-hop latency labels — sequence only (flow keeps just the
              header estimate badge). The callee's `latency` at each call's arrow
              midpoint (own time), `???` when unpriced. Dimmed + haloed, below the
              arrow so it never collides with a call comment (which rides above
              the target block). */}
          {variant === "sequence" && stepLatencies && layout.rows.map((r, i) => {
            if (r.stepIndex === null || hiddenRows.has(r)) return null;
            const sl = stepLatencies.get(r.stepIndex);
            if (!sl?.label || sl.label === "???") return null; // ??? lives in the ruler, not on arrows
            const toI = laneIndex(r.toLaneId);
            if (toI < 0) return null;
            const fromI = laneIndex(r.fromLaneId);
            const midI = fromI >= 0 && fromI !== toI ? (fromI + toI) / 2 : toI;
            const { blockCy } = blockYBounds(r.y, toI, geom, rowTopPad(r.y), rowBottomPad(r.y));
            const faded = walkResult?.byIndex.get(r.stepIndex)?.modifiers?.branchSelected === false;
            return (
              <text
                key={`lat-${i}`}
                data-latency={sl.label}
                x={midI * LANE_WIDTH + LANE_WIDTH / 2}
                y={blockCy + 13}
                fontSize={8}
                fill={sl.ownOpen ? "#f59e0b" : "#5eead4"}
                textAnchor="middle"
                paintOrder="stroke"
                stroke="#0f172a"
                strokeWidth={3}
                strokeLinejoin="round"
                opacity={faded ? 0.4 : 0.95}
                style={{ pointerEvents: "none" }}
              >
                {sl.label}
              </text>
            );
          })}
          {/* Central step-travel droplet: BFS-walks the flow graph from the
              previous active step to the current one and animates the measured
              edge chain in order (so it flows through split/merge markers). */}
          {variant === "flow" && (
            <StepDroplets
              graph={flowGraph}
              activeStepIndex={activeStepIndex}
              animSeq={animSeq}
              autoPlay={autoPlay}
              overlayRef={overlayRef}
              fromRef={dropletFromRef}
              onArrive={setHighlightStep}
            />
          )}
        </svg>
      </div>
      </div>
    </div>
  );
}

/**
 * A bare `"text"` step (no actor) is a free-floating comment — an
 * annotation on the process, not a step in the flow. A `Module "text"`
 * step IS a step (the module is "doing" the action). The discriminator
 * is `step.from.length === 0`.
 */
// Row classification (isCommentRow / isFlowNode / isFirstRealStep) + the flow
// chain now live in ./swimlaneRows (pure, unit-tested).

// HTML grid cells → ./swimlaneCells; frame/subprocess overlays → ./swimlaneFrames;
// SVG arrows/droplets → ./swimlaneArrows; lane chrome → ./swimlaneChrome.

// Label resolution lives in ./labelForStep — shared with the geometry-parity
// pin against the headless export.
